import React, { useEffect, useRef, useState } from 'react';
import ConfirmModal from './ConfirmModal';

/**
 * TagManagementModal
 * 
 * Responsibility: Encapsulates all tag CRUD operations in a modal interface
 * Single Responsibility: Tag management (create, list, delete tags)
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {Function} onClose - Callback to close modal
 * @param {Function} onTagsChanged - Callback after tags are modified
 * @param {Object} datastore - Data access object with { listTags, createTag, deleteTag }
 */
export default function TagManagementModal({ isOpen, onClose, onTagsChanged, datastore }) {
    const [tags, setTags] = useState([]);
    const [newTagName, setNewTagName] = useState('');
    const [newTagColor, setNewTagColor] = useState('#3b82f6');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [confirmModal, setConfirmModal] = useState(null);

    const nameInputRef = useRef(null);
    const previouslyFocusedRef = useRef(null);

    // Fetch tags when modal opens
    useEffect(() => {
        if (!isOpen) return;

        previouslyFocusedRef.current = document.activeElement;

        const fetchTags = async () => {
            setLoading(true);
            setError('');
            try {
                const result = await datastore.listTags();
                setTags(Array.isArray(result) ? result : []);
            } catch (err) {
                setError(err?.message || 'Failed to load tags.');
            } finally {
                setLoading(false);
                setTimeout(() => nameInputRef.current?.focus(), 0);
            }
        };

        fetchTags();
    }, [isOpen, datastore]);

    // Restore focus when closing
    useEffect(() => {
        if (!isOpen && previouslyFocusedRef.current?.focus) {
            previouslyFocusedRef.current.focus();
        }
    }, [isOpen]);

    // Close on ESC
    useEffect(() => {
        if (!isOpen) return;

        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose?.();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, onClose]);

    const refreshTags = async () => {
        const result = await datastore.listTags();
        setTags(Array.isArray(result) ? result : []);
    };

    const handleCreate = async () => {
        const trimmedName = newTagName.trim();
        if (!trimmedName) {
            setError('Tag name is required');
            return;
        }

        setLoading(true);
        setError('');
        try {
            await datastore.createTag(trimmedName, newTagColor);
            setNewTagName('');
            setNewTagColor('#3b82f6');
            await refreshTags();
            onTagsChanged?.();
        } catch (err) {
            setError(err?.message || 'Failed to create tag.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (tagId) => {
        setConfirmModal({
            title: 'Delete tag?',
            message: 'Permanently delete this tag? It will be removed from all users.',
            confirmLabel: 'Delete',
            danger: true,
            onConfirm: async () => {
                setConfirmModal(null);
                setLoading(true);
                setError('');
                try {
                    await datastore.deleteTag(tagId);
                    await refreshTags();
                    onTagsChanged?.();
                } catch (err) {
                    setError(err?.message || 'Failed to delete tag.');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) onClose?.();
    };

    if (!isOpen) return null;

    return (
        <div style={styles.overlay} onClick={handleBackdropClick}>
            <div style={styles.modal} role="dialog" aria-labelledby="modal-title" aria-modal="true">
                <div style={styles.header}>
                    <h2 id="modal-title" style={styles.title}>Manage Tags</h2>
                    <button style={styles.closeButton} onClick={onClose} aria-label="Close">×</button>
                </div>

                {error && <div style={styles.error}>{error}</div>}

                {/* Create Form */}
                <div style={styles.formRow}>
                    <input
                        ref={nameInputRef}
                        style={styles.input}
                        type="text"
                        placeholder="Tag name"
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleCreate()}
                        aria-label="Tag name"
                        disabled={loading}
                    />
                    <input
                        style={styles.colorInput}
                        type="color"
                        value={newTagColor}
                        onChange={(e) => setNewTagColor(e.target.value)}
                        aria-label="Tag color"
                        disabled={loading}
                    />
                    <button
                        style={{ ...styles.button, ...(loading && styles.buttonDisabled) }}
                        onClick={handleCreate}
                        disabled={loading}
                    >
                        Create
                    </button>
                </div>

                {/* Tags List */}
                <div style={styles.tagList} aria-live="polite">
                    {loading && tags.length === 0 && <div style={styles.loading}>Loading...</div>}
                    {tags.length === 0 && !loading && <div style={styles.empty}>No tags found.</div>}
                    {tags.map((tag) => (
                        <div key={tag.tag_id} style={styles.tagItem}>
                            <div style={styles.tagInfo}>
                                <span
                                    style={{
                                        ...styles.tagColor,
                                        backgroundColor: tag.tag_color || '#9ca3af',
                                    }}
                                />
                                <span>{tag.tag_name}</span>
                            </div>
                            <button
                                style={{ ...styles.deleteButton, ...(loading && styles.buttonDisabled) }}
                                onClick={() => handleDelete(tag.tag_id)}
                                disabled={loading}
                                aria-label={`Delete ${tag.tag_name}`}
                            >
                                Delete
                            </button>
                        </div>
                    ))}
                </div>
            </div>
            <ConfirmModal
                open={!!confirmModal}
                title={confirmModal?.title}
                message={confirmModal?.message}
                confirmLabel={confirmModal?.confirmLabel}
                danger={confirmModal?.danger}
                onConfirm={confirmModal?.onConfirm}
                onCancel={() => setConfirmModal(null)}
            />
        </div>
    );
}

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    modal: {
        backgroundColor: '#fff',
        borderRadius: 8,
        padding: 24,
        width: '100%',
        maxWidth: 500,
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
    },
    title: {
        margin: 0,
        fontSize: 20,
        fontWeight: 600,
    },
    closeButton: {
        background: 'transparent',
        border: 'none',
        fontSize: 28,
        cursor: 'pointer',
        padding: 4,
        lineHeight: 1,
        color: '#6b7280',
    },
    error: {
        backgroundColor: '#fee2e2',
        color: '#991b1b',
        padding: 12,
        borderRadius: 4,
        marginBottom: 16,
        fontSize: 14,
    },
    formRow: {
        display: 'flex',
        gap: 8,
        marginBottom: 16,
    },
    input: {
        flex: 1,
        padding: '8px 12px',
        border: '1px solid #d1d5db',
        borderRadius: 4,
        fontSize: 14,
    },
    colorInput: {
        width: 50,
        height: 36,
        border: '1px solid #d1d5db',
        borderRadius: 4,
        cursor: 'pointer',
    },
    button: {
        padding: '8px 16px',
        backgroundColor: '#3b82f6',
        color: '#fff',
        border: 'none',
        borderRadius: 4,
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 500,
    },
    buttonDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
    deleteButton: {
        padding: '6px 12px',
        backgroundColor: '#ef4444',
        color: '#fff',
        border: 'none',
        borderRadius: 4,
        cursor: 'pointer',
        fontSize: 13,
    },
    tagList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    tagItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 12,
        border: '1px solid #e5e7eb',
        borderRadius: 4,
    },
    tagInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    tagColor: {
        width: 20,
        height: 20,
        borderRadius: 3,
        border: '1px solid rgba(0,0,0,0.1)',
    },
    loading: {
        textAlign: 'center',
        color: '#6b7280',
        padding: 16,
    },
    empty: {
        textAlign: 'center',
        color: '#9ca3af',
        padding: 16,
        fontStyle: 'italic',
    },
};
